<template>
  <div>
    <ConfirmationDialog
      v-model="showDeleteDialog"
      :title="
        t('Delete - Order {orderId}', {
          orderId: order.orderId.toString(),
        })
      "
      :message="t('Are you sure you want to delete this Order?')"
      :delete-button="true"
      @confirm="deleteItemConfirm"
      @cancel="closeDeleteDialog"
    />
    <ClosableHeader @close="close()">
      {{ t("Order") }} ({{ order.orderId }})
      <span v-if="title">
        <span class="order-title-white-space">-</span>
        {{ t(title) }}
      </span>
    </ClosableHeader>
    <v-container class="order-container">
      <v-row class="text-center">
        <OrderSubmitted
          v-if="showSubmitted"
          @hideSubmitted="showSubmitted = false"
          @newPatientOrder="newPatientOrder()"
          @close="close()"
        />
        <OrderSummary
          v-if="showSummary"
          @hideSummary="showSummary = false"
          @sumbitOrder="submitOrder()"
          @cancelOrder="cancelOrder()"
        />
        <FormRenderer
          v-if="order.form && !isInitializingOrder"
          v-show="!showSummary && !showSubmitted"
          @patientSelected="updateOrderPatientData($event)"
          @saveOrder="saveOrder([])"
          @showSummary="showSummary = true"
          @cancelOrder="cancelOrder()"
        />
      </v-row>
    </v-container>
  </div>
</template>

<script setup lang="ts">
import {  ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useOrdersStore } from "@/stores/orders";
import { usePatientsStore } from "@/stores/patients";
import { usePhysiciansStore } from "@/stores/physicians";
import { useFormStore } from "@/stores/form";
import { usePermissionsService } from '@/composables/usePermissionsService';
import {FORM_CONTROLS, ORDER_STATUS} from "@/utils/constants";
import { Patient } from "@/entities/patient";
import { JsonForm } from "@/entities/json-form";
import { FormControl } from "@/entities/form-control";
import FormRenderer from "@/components/FormRenderer.vue";
import ClosableHeader from "@/components/common/ClosableHeader.vue";
import OrderSummary from "@/components/orders/OrderSummary.vue";
import OrderSubmitted from "@/components/orders/OrderSubmitted.vue";
import ConfirmationDialog from "@/components/dialogs/ConfirmationDialog.vue";
import NotificationMessageService from "@/services/notificationmessage.service";
import { convertFormBuilderSexValueToFrontendValue } from "@/utils";
import { toIsoDate } from "@/composables/useDateHelper"
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";

defineOptions({ name: "Order" });

const { t } = useI18n();
const ordersStore = useOrdersStore();
const physiciansStore = usePhysiciansStore();
const patientsStore = usePatientsStore();
const formStore = useFormStore();
const router = useRouter();
const route = useRoute();
const permissionsService = usePermissionsService();

// Reactive state
const showSummary = ref(false);
const showSubmitted = ref(false);
const showDeleteDialog = ref(false);
const isInitializingOrder = ref(false);

// Computed
const { order } = storeToRefs(ordersStore);
const getOrderFormControls = computed(():FormControl[] => ordersStore.getOrderFormControls);
const getForm = computed(():JsonForm => formStore.form);
const getPhysician = computed(() => physiciansStore.physician);

const title = computed(() => order.value.formTestTitle ?? order.value.form?.formConfig?.name ?? "");
const pathPart = computed(() => route.fullPath.split("/")[2]?.toLowerCase() || "");
const isNewOrder = computed(() => pathPart.value === "new");
const isEditOrder = computed(() => pathPart.value === "edit");
const isViewOrder = computed(() => pathPart.value === "view");
const isDraft = computed(() => order.value.statusId === ORDER_STATUS.draft);
const formId = computed(() => route.params.formId as string);
const testId = computed(() => Number(route.params.testId));
const orderId = computed(() => route.params.orderId as string);
const getPhysicianId = computed(() => <string>getPhysician.value?.userId);
const getOrderFormControlByName = ordersStore.getOrderFormControlByName;

/**
 * Checks if the form has an active notification trigger.
 * @returns {number[]} - recipient group ids of all controls with notification triggers have their current values included
 * in their notification triggers, empty array otherwise.
 */
const collectActiveRecipientGroups = computed(() => {
  const controls = getOrderFormControls.value;
  if (!controls || !Array.isArray(controls)) {
    return [];
  }

  const controlsWithTrigger = controls.filter((c: FormControl) => c.notificationTriggers?.length);
  if (!controlsWithTrigger.length) {
    return [];
  }

  const recipientGroupIds: number[] = [];

  controlsWithTrigger.forEach((control: FormControl) => {
    const controlValueAsString = String(control.value);

    if (control.notificationTriggers.includes(controlValueAsString)) {
      control.recipientGroups.forEach((groupId: number) => {
        if (!recipientGroupIds.includes(groupId)) {
          recipientGroupIds.push(groupId);
        }
      });
    }
  });

  return recipientGroupIds;
});

const checkPermissions = () => {
  if (
    (isNewOrder.value && !hasCreatePermission()) ||
    (isEditOrder.value && !hasEditPermission()) ||
    (isViewOrder.value && !hasViewPermission())
  ) {
    NotificationMessageService.showWarning("Not Enough Permissions");
    router.push({ path: "/" });
  }
};

const hasCreatePermission = () => {
  return (
    (permissionsService.hasOrderCreatePermission && order.value.statusId === ORDER_STATUS.draft) ||
    permissionsService.hasOrderCreateAllPermission
  );
};

const hasEditPermission = () => {
  return (
    (permissionsService.hasOrderUpdatePermission && order.value.statusId === ORDER_STATUS.draft) ||
    permissionsService.hasOrderUpdateAllPermission
  );
};

const hasViewPermission = () => {
  return (
    (permissionsService.hasOrderReadPermission && order.value.statusId !== ORDER_STATUS.draft) ||
    permissionsService.hasOrderReadAllPermission
  );
};

const loadOrder = async () => {
  if (isNewOrder.value && formId.value) {
    isInitializingOrder.value = true;
    // load form / convert into order
    await fetchForm();

    // save order to get the orderId
    await postOrder();
    isInitializingOrder.value = false;
    // returning the ID = saved successfully
    if (order.value.orderId) {
      await router.push(`/order/edit/${order.value.orderId}`);
    }
  } else if ((isEditOrder.value || isViewOrder.value) && orderId.value) {
    ordersStore.resetOrder();
    await ordersStore.fetchOrder([Number(orderId.value), getPhysicianId.value]);
    await ordersStore.fetchOrderSupportingFiles(Number(orderId.value));
    if (order.value.patientId)
      await patientsStore.fetchPatient(order.value.patientId, getPhysicianId.value);
  }
};

const fetchForm = async () => {
  await formStore.fetchForm([formId.value, testId.value]);
}

async function postOrder() {
  updateOrderInitialData();
  await ordersStore.postOrder(getPhysicianId.value);
}

const updateOrderInitialData = (): void => {
  if (!order.value) return
  order.value.statusId = "draft"
  order.value.formTestId = testId.value
  order.value.form = getForm.value
}

const updateOrderPatientData = (patient: Patient) => {
  if (isInitializingOrder.value) return;

  ordersStore.setOrderPatient(patient);
};

const mapPatientFromControlsToOrder = () => {
  const controls = ordersStore.getOrderFormControls;

  if (!controls?.length || !order.value) return;

  order.value.patientFirstName =
    getOrderFormControlByName(FORM_CONTROLS.patient_firstName)?.value ?? null;

  order.value.patientLastName =
    getOrderFormControlByName(FORM_CONTROLS.patient_lastName)?.value ?? null;

  order.value.patientDateOfBirth =
    getOrderFormControlByName(FORM_CONTROLS.patient_dateOfBirth)?.value ?? null;

  order.value.patientSexId =
    convertFormBuilderSexValueToFrontendValue(
      getOrderFormControlByName(FORM_CONTROLS.patient_sexId)?.value ?? null
    )

  order.value.referenceId =
    getOrderFormControlByName(FORM_CONTROLS.patient_externalId)?.value ?? null;
};

const saveOrder = async(recipientGroupIds: number[]): Promise<void> => {
  if (order.value.orderId === 0) {
    await ordersStore.postOrder(getPhysicianId.value);
  }
  await ordersStore.putOrder([true, getPhysicianId.value, recipientGroupIds]);
};

const submitOrder = async (): Promise<void> => {
  if (isDraft.value) {
    mapPatientFromControlsToOrder();

    if (!order.value.patientId) {
      await registerNewPatient()
      if (!order.value.patientId) {
        NotificationMessageService.showError('Patient registration failed.')
        return
      }
    }
    order.value.statusId = "order_submitted";

    const recipientGroupIds = collectActiveRecipientGroups.value;
    await saveOrder(recipientGroupIds);
  }

  showSummary.value = false;
  showSubmitted.value = true;
};

const registerNewPatient = async (): Promise<void> => {
  patientsStore.resetPatient();

  const newPatient: Patient = {
    patientId: 0,
    firstName: order.value.patientFirstName,
    lastName: order.value.patientLastName,
    dateOfBirth: toIsoDate(order.value.patientDateOfBirth) ?? "",
    sexId: order.value.patientSexId,
    externalId: order.value.referenceId,
  };

  const createdPatient = await patientsStore.postPatient(
    newPatient,
    getPhysicianId.value
  );

  if (!createdPatient?.patientId) {
    return;
  }
  updateOrderPatientData(createdPatient);
};

const close = (): void => {
  if (isDraft.value && !isViewOrder.value) {
    saveOrder([]);
  }
  ordersStore.resetOrder();
  router.push("/MyOrders");
};

const cancelOrder = (): void => {
  showDeleteDialog.value = true;
};

const newPatientOrder = async (): Promise<void> => {
  ordersStore.resetOrderSupportingFiles();
  ordersStore.resetOrder();
  ordersStore.clearOrderPatient()
  await router.push({path: `/order/new`});
};

const deleteItemConfirm = async () => {
  await ordersStore.deleteOrder([order.value.orderId, getPhysicianId.value]);
  closeDeleteDialog();
  await router.push("/MyOrders");
};

const closeDeleteDialog = () => {
  showDeleteDialog.value = false;
};

onMounted(async () => {
  ordersStore.resetOrder();
  await loadOrder();
  checkPermissions();
});
</script>

<style lang="scss">
  .order-container {
    max-width: 100%;

    .order-title-white-space {
      padding: 0 1.5625rem;
    }

    @media #{map.get($display-breakpoints, 'sm-and-down')} {
      > .row {
        margin-left: 0;
        margin-right: 0;
      }
    }
  }
</style>
